import { IWebSocketMessage, WebSocketMessage } from './WebSocketMessage';
import { WebSocketRequest, WebSocketRequestOptions } from './WebSocketRequest';
import * as Rx from 'rxjs';

export abstract class WebSocketClient {

    protected channel: string;
    protected url: string;
    protected webSocket: WebSocket;
    protected messageBuffer: { [id: string]: WebSocketRequest } = {};
    protected reconnectTimeout;

    public messages: Rx.Subject<IWebSocketMessage>;

    public onOpened: () => void;

    protected boundOnOpen: EventListener;
    protected boundOnClose: EventListener;
    protected boundOnError: EventListener;
    protected boundDispatch: EventListener;

    protected constructor(channel: string, url: string) {
        this.channel = channel;
        this.url = url;
        this.messages = new Rx.Subject<IWebSocketMessage>();
    }

    public matchUrl(url: string): boolean {
        return this.url.includes(url);
    }

    public connect(): void {
        this.webSocket = new WebSocket(this.url);
        this.boundOnOpen = this.onOpen.bind(this);
        this.boundOnClose = this.onClose.bind(this);
        this.boundOnError = this.onError.bind(this);
        this.boundDispatch = this.dispatch.bind(this);
        this.webSocket.addEventListener('open', this.boundOnOpen);
        this.webSocket.addEventListener('close', this.boundOnClose);
        this.webSocket.addEventListener('error', this.boundOnError);
        this.webSocket.addEventListener('message', this.boundDispatch);
    }

    public disconnect(): void {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }

        if (this.webSocket) {
            this.webSocket.removeEventListener('open', this.boundOnOpen);
            this.webSocket.removeEventListener('close', this.boundOnClose);
            this.webSocket.removeEventListener('error', this.boundOnError);
            this.webSocket.removeEventListener('message', this.boundDispatch);

            this.webSocket.close();
        }
    }

    public getUrl(): string {
        return this.url;
    }

    public send(message: IWebSocketMessage): void {
        if (this.isOpen()) {
            if (!message.message_channel) {
                message.message_channel = this.channel;
            }
            this.webSocket.send(message.stringify());
        } else {
            console.warn('WebSocketClient::send - Attempt to send data to closed WebSocket');
        }
    }

    public sendWithResponse(message: IWebSocketMessage, options?: WebSocketRequestOptions): Promise<WebSocketMessage> {
        let request: WebSocketRequest = new WebSocketRequest(this, message, options);
        this.messageBuffer[message.message_id] = request;
        request.onCompleted((id: string) => {
            delete this.messageBuffer[message.message_id];
        });
        return request.send();
    }

    public isOpen(): boolean {
        return this.webSocket && this.webSocket.readyState === WebSocket.OPEN;
    }

    protected onOpen(event) {
        if (this.onOpened) {
            this.onOpened();
        }
    }

    protected onError(event) {}

    protected onClose(event) {
        this.reconnectAfterTimeout();
    }

    protected dispatch(event): void {
        try {
            let message: WebSocketMessage = new WebSocketMessage(JSON.parse(event.data));

            if (this.messageBuffer.hasOwnProperty(message.message_id)) {
                this.messageBuffer[message.message_id].resolve(message);
            } else {
                this.onMessage(message);
                this.messages.next(message);
            }

        } catch (error) {
            console.error(error);
        }
    }

    protected reconnectAfterTimeout(): void {

        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }

        this.reconnectTimeout = setTimeout(() => {
            this.connect();
        }, 5000);
    }

    protected abstract onMessage(message: WebSocketMessage);
}

export class WSMessagePing extends WebSocketMessage {
    constructor() {
        super('ping');
    }
}
